import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useBuildBomCartItemsMutation,
  useRematchBomMutation,
  useUpdateBomItemMutation,
  useUploadBomMutation,
} from "../../redux/api/bomApi";
import "./BomOrderPage.css";

const STATUS_LABELS = {
  all: "All",
  matched: "Matched",
  pending_confirm: "Pending",
  unmatched: "Unmatched",
  invalid: "Invalid",
};

/**
 * 获取 BOM 本地会话 ID，游客上传时用于访问自己的 BOM 任务。
 * @returns {string} BOM 会话 ID
 */
const getBomSessionId = () => {
  const storageKey = "bomOrderSessionId";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const nextId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 格式化金额显示。
 * @param {number|string} value 原始金额
 * @returns {string} 金额文本
 */
const formatMoney = (value) => {
  const parsed = Number(value || 0);
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

/**
 * 获取 BOM 行状态显示文本。
 * @param {string} status 状态值
 * @returns {string} 状态文本
 */
const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status || "-";
};

const BomOrderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sessionId] = useState(getBomSessionId);
  const [bomOrder, setBomOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [uploadBom, { isLoading: isUploading }] = useUploadBomMutation();
  const [updateBomItem] = useUpdateBomItemMutation();
  const [rematchBom, { isLoading: isRematching }] = useRematchBomMutation();
  const [buildBomCartItems, { isLoading: isAdding }] = useBuildBomCartItemsMutation();

  const filteredItems = useMemo(() => {
    const items = bomOrder?.items || [];
    if (statusFilter === "all") return items;
    return items.filter((item) => item.matchStatus === statusFilter);
  }, [bomOrder?.items, statusFilter]);

  const selectedCount = selectedItemIds.size;

  /**
   * 上传 BOM 文件并刷新页面结果。
   * @param {File} file BOM 文件
   */
  const handleUploadFile = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sessionId", sessionId);

    try {
      const response = await uploadBom(formData).unwrap();
      setBomOrder(response?.bomOrder || null);
      const defaultSelected = new Set(
        (response?.bomOrder?.items || [])
          .filter((item) => item.selected)
          .map((item) => item._id)
      );
      setSelectedItemIds(defaultSelected);
      toast.success("BOM parsed successfully");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to upload BOM");
    }
  };

  /**
   * 处理文件输入框变化。
   * @param {Event} event 输入事件
   */
  const handleFileChange = (event) => {
    handleUploadFile(event.target.files?.[0]);
    event.target.value = "";
  };

  /**
   * 切换单行选择状态。
   * @param {Object} item BOM 明细
   * @param {boolean} checked 是否选中
   */
  const toggleItemSelection = async (item, checked) => {
    const nextIds = new Set(selectedItemIds);
    if (checked) {
      nextIds.add(item._id);
    } else {
      nextIds.delete(item._id);
    }
    setSelectedItemIds(nextIds);

    try {
      await updateBomItem({
        bomId: bomOrder._id,
        itemId: item._id,
        sessionId,
        payload: { selected: checked },
      }).unwrap();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update BOM item");
    }
  };

  /**
   * 修改 BOM 行购买数量。
   * @param {Object} item BOM 明细
   * @param {number|string} quantity 新数量
   */
  const updateItemQuantity = async (item, quantity) => {
    try {
      const response = await updateBomItem({
        bomId: bomOrder._id,
        itemId: item._id,
        sessionId,
        payload: { quantity },
      }).unwrap();
      setBomOrder(response?.bomOrder || bomOrder);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update quantity");
    }
  };

  /**
   * 重新匹配当前 BOM 任务。
   */
  const handleRematch = async () => {
    if (!bomOrder?._id) return;

    try {
      const response = await rematchBom({ bomId: bomOrder._id, sessionId }).unwrap();
      setBomOrder(response?.bomOrder || null);
      setSelectedItemIds(new Set((response?.bomOrder?.items || []).filter((item) => item.selected).map((item) => item._id)));
      toast.success("BOM rematched");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to rematch BOM");
    }
  };

  /**
   * 将已选 BOM 明细批量加入现有购物车。
   */
  const addSelectedToCart = async () => {
    if (!bomOrder?._id || selectedItemIds.size === 0) return;

    try {
      const response = await buildBomCartItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds: Array.from(selectedItemIds),
      }).unwrap();

      (response?.cartItems || []).forEach((cartItem) => dispatch(setCartItem(cartItem)));
      toast.success(`${response?.cartItems?.length || 0} BOM item(s) added to cart`);
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add BOM items to cart");
    }
  };

  const summary = bomOrder?.summary || {};

  return (
    <main className="bom-order-page">
      <div className="bom-order-shell">
        <section className="bom-order-toolbar">
          <div className="bom-order-title">
            <h1>BOM Order</h1>
            <p>Upload a BOM file, match parts, review pricing, then add confirmed items to cart.</p>
          </div>
          {bomOrder?._id && (
            <button
              type="button"
              className="bom-order-action"
              onClick={handleRematch}
              disabled={isRematching}
            >
              {isRematching ? "Rematching..." : "Rematch"}
            </button>
          )}
        </section>

        <section className="bom-order-upload">
          <label className="bom-order-dropzone">
            <input
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <span className={`bom-order-upload-button ${isUploading ? "is-disabled" : ""}`}>
              {isUploading ? "Uploading..." : "Upload BOM"}
            </span>
            <span className="bom-order-meta">Supports xls, xlsx, csv. Max file size: 10M.</span>
          </label>
        </section>

        {bomOrder ? (
          <>
            <section className="bom-order-summary">
              <div className="bom-order-summary-item">
                <span>Total</span>
                <strong>{summary.totalRows || 0}</strong>
              </div>
              <div className="bom-order-summary-item">
                <span>Matched</span>
                <strong>{summary.matchedRows || 0}</strong>
              </div>
              <div className="bom-order-summary-item">
                <span>Pending</span>
                <strong>{summary.pendingRows || 0}</strong>
              </div>
              <div className="bom-order-summary-item">
                <span>Unmatched</span>
                <strong>{summary.unmatchedRows || 0}</strong>
              </div>
              <div className="bom-order-summary-item">
                <span>Invalid</span>
                <strong>{summary.invalidRows || 0}</strong>
              </div>
              <div className="bom-order-summary-item">
                <span>Total Amount</span>
                <strong>${formatMoney(summary.totalAmount)}</strong>
              </div>
            </section>

            <section className="bom-order-controls">
              <div className="bom-order-filters">
                {Object.keys(STATUS_LABELS).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={`bom-order-filter ${statusFilter === status ? "is-active" : ""}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="bom-order-action"
                onClick={addSelectedToCart}
                disabled={selectedCount === 0 || isAdding}
              >
                {isAdding ? "Adding..." : `Add selected to cart (${selectedCount})`}
              </button>
            </section>

            <section className="bom-order-table-wrap">
              <table className="bom-order-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Line</th>
                    <th>Original Part</th>
                    <th>Designator</th>
                    <th>Supplier Part</th>
                    <th>Qty</th>
                    <th>Matched Product</th>
                    <th>Stock</th>
                    <th>Unit Price</th>
                    <th>Subtotal</th>
                    <th>Status</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="bom-order-empty">No BOM items</td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const canSelect = item.matchStatus === "matched" && item.selectedProduct && !item.needQuote;
                      return (
                        <tr key={item._id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedItemIds.has(item._id)}
                              disabled={!canSelect}
                              onChange={(event) => toggleItemSelection(item, event.target.checked)}
                            />
                          </td>
                          <td>{item.lineNo}</td>
                          <td className="bom-order-part">
                            <strong>{item.normalized?.originalMpn || "-"}</strong>
                            <span>{item.normalized?.originalManufacturer || "-"}</span>
                          </td>
                          <td>{item.normalized?.designator || "-"}</td>
                          <td>{item.normalized?.originalSupplierPartNo || "-"}</td>
                          <td>
                            <input
                              className="bom-order-qty-input"
                              type="number"
                              min="1"
                              value={item.quantity || 1}
                              onChange={(event) => updateItemQuantity(item, event.target.value)}
                              disabled={!item.selectedProduct}
                            />
                          </td>
                          <td className="bom-order-part">
                            <strong>{item.productSnapshot?.name || "-"}</strong>
                            <span>{item.productSnapshot?.Manufacturer_Part_Number || ""}</span>
                          </td>
                          <td>{item.productSnapshot?.stock || "-"}</td>
                          <td>${formatMoney(item.unitPrice)}</td>
                          <td>${formatMoney(item.subtotal)}</td>
                          <td>
                            <span className={`bom-status bom-status-${item.matchStatus}`}>
                              {getStatusLabel(item.matchStatus)}
                            </span>
                          </td>
                          <td>{item.matchReason || item.warnings?.join("; ") || "-"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </section>
          </>
        ) : (
          <section className="bom-order-table-wrap">
            <div className="bom-order-empty">Upload a BOM file to start matching parts.</div>
          </section>
        )}
      </div>
    </main>
  );
};

export default BomOrderPage;
